/**
 * As escritas sobre os documentos exigidos (`TP-057`) e a mensagem de cada recusa. Fora dos componentes de propósito:
 * um arquivo que exporta componente e função quebra o fast refresh.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ErroDaApi } from '../../../shared/api/erros';
import { invalidarOQueAProvaMuda } from '../completude/api';
import { agirNoDocumento, chavesDeDocumentos, enviarDocumento } from './api';
import type { AcaoNoDocumento } from './tipos';

/** A mensagem da recusa do back NOMEIA o que aconteceu; reescrevê-la aqui perderia isso. */
export function mensagemDeErroDeDocumento(causa: unknown): string {
  if (causa instanceof ErroDaApi) {
    if (causa.codigo === 'DOCUMENT_TOO_LARGE') return 'O arquivo é maior do que a plataforma aceita. Envie um menor.';
    if (causa.codigo === 'DOCUMENT_READING_IN_PROGRESS')
      return 'O arquivo anterior ainda está sendo lido. Espere a leitura terminar para enviar outro.';
    if (causa.codigo === 'DOCUMENT_REASON_REQUIRED')
      return 'Diga o motivo: sem ele a ação não fica explicada no histórico.';
    if (causa.status === 404) return 'Este documento ou item não é mais deste caso. Recarregue a página.';
    if (causa.status === 409) return causa.message;
    if (causa.status === 400) return causa.message;
  }
  return 'Não foi possível concluir. Tente novamente.';
}

export function useAcoesDeDocumento(casoId: string) {
  const cliente = useQueryClient();
  // O documento muda a completude (TP-058) e, com o passo provado fechando sozinho, o passo da frente e a linha do tempo.
  const aplicar = (visao: Awaited<ReturnType<typeof agirNoDocumento>>): void => {
    cliente.setQueryData(chavesDeDocumentos.doCaso(casoId), visao);
    void invalidarOQueAProvaMuda(cliente, casoId, 'documentos');
  };

  const agir = useMutation({
    mutationFn: (p: { exigidoId: string; acao: AcaoNoDocumento; motivo?: string; confirmado?: boolean }) =>
      agirNoDocumento(casoId, p.exigidoId, p.acao, p.motivo, p.confirmado),
    onSuccess: aplicar,
  });

  // O upload só REGISTRA; a leitura é da próxima rodada. A visão é relida para o documento aparecer "aguardando leitura".
  const enviar = useMutation({
    mutationFn: (p: { arquivo: File; exigidoId?: string }) => enviarDocumento(casoId, p.arquivo, p.exigidoId),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: chavesDeDocumentos.doCaso(casoId) });
      await invalidarOQueAProvaMuda(cliente, casoId, 'documentos');
    },
  });

  return { agir, enviar };
}
